import Icon from '@components/Icon';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { ButtonHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
    fullWidth?: boolean;
    variant?: 'primary' | 'secondary' | 'outline' |'sub' | 'sub-outline' | 'tertiary';
    size?: 'sm' | 'md' | 'lg';
    leftIcon?: string;
    color?: string;
    backgroundColor?: string;
    noPadding?: boolean;
}

function Button(props: Props) {
    const {
        fullWidth, variant, children, size, leftIcon, color, backgroundColor, className,
        noPadding,
        ...rest
    } = props;
    const { i18n: { language } } = useTranslation();
    return (<Base
        lang={language}
        color={color}
        backgroundColor={backgroundColor}
        className={classNames(
            `variant-${variant}`,
            `button-${fullWidth ? 'full' : 'auto'}`,
            `size-${size}`,
            { 'no-padding': noPadding },
            className,
        )}
        {...rest} >
        {children}
        {leftIcon && (
            <LeftIcon name={leftIcon}/>
        )}
    </Base>);
}

const Base = styled.button<{
    color?: string;
    backgroundColor?: string;
}>`
    align-items: center;
    background: var(--color-primary);
    border-radius: 20px;
    color: var(--color-button-text);
    cursor: pointer;
    display: flex;
    font-size: 16px;
    font-weight: bold;
    height: 56px;
    justify-content: center;
    line-height: 1.25;
    padding: 0 50px;
    position: relative;
    transition: 150ms;
    white-space: nowrap;

    &:active:not(:disabled) {
        transform: scale(.95);
    }

    &.size-sm {
        height: 50px;
    }

    &.variant-secondary {
        background: var(--color-secondary);
        color: var(--color-button-text);
    }


    &.variant-outline {
        background: var(--color-white);
        border: 1px solid var(--color-primary);
        color: var(--color-primary);
    }

    &.variant-tertiary {
        background-color: var(--color-gray3);
        color: var(--color-gray6);
    }

    &.variant-sub {
        background: var(--color-gray1);
        color: var(--color-gray7);
        font-size: 15px;
        font-weight: 400;
        height: 50px;
    }

    &.variant-sub-outline {
        background: var(--color-white);
        border: 1px solid var(--color-gray3);
        color: var(--color-gray7);
        font-size: 15px;
        font-weight: 400;
        height: 50px;
    }

    &.button-full {
        width: 100%;
    }

    &.no-padding {
        padding: 0;
    }

    &:disabled {
        border: none !important;
        background-color: var(--color-gray3);
        color: var(--color-gray6);
        cursor: not-allowed;
    }
    
    ${({ color }) => (color ? css`
        color: ${color};
    ` : undefined)}

    ${({ backgroundColor }) => (backgroundColor ? css`
        background-color: ${backgroundColor};
    ` : undefined)}
`;

const LeftIcon = styled(Icon)`
    left: 20px;
    position: absolute;
`;

export default Button;
